/**
 * Captures Service
 *
 * Pure business logic for capture/inbox operations and transformations.
 * No React or Firebase dependencies.
 */

import { Capture, CaptureStatus } from '@/types';

// ============================================
// Capture Filtering
// ============================================

/**
 * Filter captures by their status.
 *
 * @param captures - Array of captures to filter
 * @param status - Status to filter by, or 'all' to return all captures
 * @returns Filtered array of captures
 */
export function filterCapturesByStatus(
  captures: Capture[],
  status: CaptureStatus | 'all'
): Capture[] {
  if (status === 'all') {
    return captures;
  }
  return captures.filter((capture) => capture.status === status);
}

/**
 * Get captures that are in the inbox (unprocessed).
 *
 * @param captures - Array of captures to filter
 * @returns Array of inbox captures
 */
export function getInboxCaptures(captures: Capture[]): Capture[] {
  return captures.filter((capture) => capture.status === 'inbox');
}

/**
 * Get captures that have been processed.
 *
 * @param captures - Array of captures to filter
 * @returns Array of processed captures
 */
export function getProcessedCaptures(captures: Capture[]): Capture[] {
  return captures.filter((capture) => capture.status === 'processed');
}

/**
 * Get captures that have been archived.
 *
 * @param captures - Array of captures to filter
 * @returns Array of archived captures
 */
export function getArchivedCaptures(captures: Capture[]): Capture[] {
  return captures.filter((capture) => capture.status === 'archived');
}

// ============================================
// Capture Counting
// ============================================

/**
 * Get the count of inbox captures.
 *
 * @param captures - Array of captures
 * @returns Number of captures in inbox
 */
export function getInboxCount(captures: Capture[]): number {
  return captures.filter((capture) => capture.status === 'inbox').length;
}

// ============================================
// Capture Sorting
// ============================================

/**
 * Sort captures by creation date (newest first).
 *
 * @param captures - Array of captures to sort
 * @returns New sorted array (does not mutate original)
 */
export function sortCapturesByDate(captures: Capture[]): Capture[] {
  return [...captures].sort(
    (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
  );
}

/**
 * Sort captures by creation date (oldest first).
 *
 * @param captures - Array of captures to sort
 * @returns New sorted array (does not mutate original)
 */
export function sortCapturesByDateAsc(captures: Capture[]): Capture[] {
  return [...captures].sort(
    (a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()
  );
}
